import React, { useEffect, useState } from "react";
import AWS from "aws-sdk";
import { useOutletContext } from "react-router-dom";
import { listRoomsInProject } from "../api/ProjectOntologyManager";

/**
 * RoomPickerMessage
 * Deterministically fetches rooms for a project and renders a simple picker.
 *
 * Props:
 * - projectId: string (required)
 * - onPicked: (room) => void (required)
 */
export default function RoomPickerMessage({ projectId, onPicked }) {
  const { userId, awsCreds } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!projectId) return;
      setLoading(true);
      setError(null);
      try {
        const list = await listRoomsInProject({
          userId,
          projectUuid: projectId,
          awsCreds, // pass the fresh creds to your API call
        });
        if (!cancelled) setRooms(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load rooms");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return (
    <div className="chat-message ai">
      <strong>AI:</strong>{" "}
      <div className="room-picker">
        {loading ? (
          <p>Loading rooms…</p>
        ) : error ? (
          <div className="error" style={{ color: "#b00020" }}>
            {error}
          </div>
        ) : rooms.length === 0 ? (
          <p>
            I couldn’t find any rooms in this project yet. Add a room from the
            Rooms page, then come back here to continue.
          </p>
        ) : (
          <>
            <p>I found rooms in your project. Pick one to start:</p>
            <div
              className="room-buttons"
              style={{
                marginTop: "0.5rem",
                display: "flex",
                gap: "0.5rem",
                flexWrap: "wrap",
              }}
            >
              {rooms.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className="room-button"
                  onClick={() => onPicked?.(r)}
                  style={{
                    padding: "0.4rem 0.8rem",
                    borderRadius: "0.5rem",
                    border: "1px solid var(--lh-border, #ccc)",
                    background: "var(--lh-surface, #f7f7f8)",
                    cursor: "pointer",
                  }}
                  aria-label={`Pick room ${r.title || r.id}`}
                >
                  {r.title || r.id}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
